'use client';

import type { CalendarEvent, LensId, EntityOption } from '@/lib/types';
import type { TickerItem } from '@/lib/event-filters';
import type { TopPage } from '@/lib/related-pages';
import { uiFb } from '@/lib/i18n';
import { getTickerPadding } from '@/lib/layout-utils';
import { useCalendarViewLogic } from '@/hooks/useCalendarViewLogic';
import { CascadingFilter } from './CascadingFilter';
import { CalendarToolbar } from './CalendarToolbar';
import { PillNav } from '../nav/PillNav';
import { ShareExportToolbar } from '../features/ShareExportToolbar';
import { BookmarkButton } from '../features/BookmarkButton';
import { ReportButton } from '../feedback/ReportButton';
import { LandingNav } from '../layout/LandingNav';
import { LandingShowcase } from '../landing/LandingShowcase';
import { LandingFooter } from '../layout/LandingFooter';
import { LanguageLinks } from '../seo/LanguageLinks';
import { WorldEventsTicker } from '../nav/WorldEventsTicker';

interface ContentCalendarViewProps {
  lens: LensId;
  entity?: string;
  entities: Record<LensId, EntityOption[]>;
  lang?: string;
  countryCode?: string;
  contentAfterShowcase?: React.ReactNode;
  contentBeforeCalendar?: React.ReactNode;
  /** Rendered after the H1/title block and before the calendar grid. */
  contentAfterTitle?: React.ReactNode;
  hideLenses?: boolean;
  events?: CalendarEvent[];
  topPages?: TopPage[];
  initialFilter?: string;
  tickerItems?: TickerItem[];
  localTickerItems?: TickerItem[];
  /** Server-computed "today" (YYYY-MM-DD) for the ticker countdowns. Passing it
   *  from the server avoids a hydration mismatch in WorldEventsTicker. */
  tickerReferenceDate?: string;
  eventSlugMap?: Record<string, string>;
  pageSlug?: string;
  pageTitle?: string;
  pageCountry?: string;
  entityLabelOverride?: string;
  entityIconOverride?: string;
  lensLabelOverride?: string;
}

export function ContentCalendarView({
  lens,
  entity = '',
  entities,
  lang = 'en',
  countryCode: countryCodeProp,
  contentAfterShowcase,
  contentBeforeCalendar,
  contentAfterTitle,
  hideLenses,
  events: eventsProp,
  topPages,
  initialFilter,
  tickerItems,
  localTickerItems,
  tickerReferenceDate,
  eventSlugMap,
  pageSlug,
  pageTitle,
  pageCountry,
  entityLabelOverride,
  entityIconOverride,
  lensLabelOverride,
}: ContentCalendarViewProps) {
  const {
    year,
    filteredEvents,
    activeFilter,
    setActiveFilter,
    filterConfig,
    availableYears,
    longWeekendHighlights,
    longWeekends,
    entityLabel,
    entityIcon,
    langAlternates,
    contentNavLinks,
    navConfig,
    countryCode,
  } = useCalendarViewLogic({
    lens,
    entity,
    entities,
    lang,
    mode: 'content',
    countryCode: countryCodeProp,
    events: eventsProp,
    initialFilter,
  });

  const finalLabel = entityLabelOverride ?? entityLabel;
  const finalIcon = entityIconOverride ?? entityIcon;

  return (
    <div className="flex min-h-screen flex-col bg-surface-root text-text-primary">
      <LandingNav
        navLinks={contentNavLinks}
        lang={lang}
        langAlternates={langAlternates}
        activeCountryCode={lens === 'country' ? entity : undefined}
        activeCityId={lens === 'city' ? entity : undefined}
        showLanguage={navConfig.showLanguage}
        showCountry={navConfig.showCountry}
        showCity={navConfig.showCity}
        showBrowse={navConfig.showBrowse}
        showSearch={navConfig.showSearch}
      />

      {/* Academic pages render their "Upcoming" strip inline in the page body
       *  (see renderUniversityPage) rather than as a fixed band, so it no longer
       *  eats the mobile fold. Non-academic pages keep the world-events ticker. */}
      {lens !== 'academic' && tickerItems && tickerItems.length > 0 && (
        <WorldEventsTicker
          items={tickerItems}
          localItems={localTickerItems}
          lang={lang}
          referenceDate={tickerReferenceDate ?? tickerItems[0].dateStart}
        />
      )}

      <main id="main-content" className={getTickerPadding(
        lens !== 'academic' && !!(tickerItems && tickerItems.length > 0),
        lens !== 'academic' && !!(localTickerItems && localTickerItems.length > 0),
      )}>
        {contentBeforeCalendar}
        <div className="px-4 pt-8 pb-0 md:px-8">
          <div className="mx-auto max-w-6xl">
          {/* Title — full width, left-aligned */}
          <h1 className="font-heading text-2xl font-black tracking-tight text-text-primary md:text-3xl">
            {finalIcon} {finalLabel} — {lens === 'academic'
              ? uiFb('ui.calendar.academic_calendar', 'Academic Calendar {range}', lang, { range: `${year}/${year + 1}` })
              : `${year} ${uiFb('ui.calendar.calendar', 'Calendar', lang)}`}
          </h1>

          {/* Subtitle + inline actions */}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-sm text-text-secondary">
              {lens === 'city'
                ? uiFb('ui.calendar.festivals_events', '{count} festivals & events', lang, { count: filteredEvents.length })
                : lens === 'academic'
                  ? uiFb('ui.calendar.academic_dates', '{count} academic dates', lang, { count: filteredEvents.length })
                  : uiFb('ui.event.summary', '{count} events', lang, { count: filteredEvents.length })}
            </p>
            {(pageSlug || pageTitle) && (
              <>
                <span className="hidden sm:block h-4 w-px bg-border-subtle" aria-hidden="true" />
                <div className="flex items-center gap-1.5">
                  <ShareExportToolbar title={pageTitle || finalLabel} bare />
                  {pageSlug && pageTitle && (
                    <>
                      <BookmarkButton pageSlug={pageSlug} pageTitle={pageTitle} eventCountry={pageCountry} compact />
                      <ReportButton pageSlug={pageSlug} pageTitle={pageTitle} pageCountry={pageCountry} pageType="page" />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          {filterConfig && filterConfig.navLinks.length > 0 && (
            <PillNav links={filterConfig.navLinks} lang={lang} />
          )}
          <div className="mt-6">
            <CalendarToolbar
              filterContent={filterConfig && filterConfig.filterOptions.length > 0 && !hideLenses ? (
                <CascadingFilter
                  options={filterConfig.filterOptions}
                  active={activeFilter}
                  onChange={setActiveFilter}
                  label={uiFb('ui.filter.show', 'Show', lang)}
                  bare
                  responsive
                />
              ) : undefined}
              availableYears={availableYears}
              events={filteredEvents}
              exportTitle={lens === 'academic' ? `${finalLabel} Academic Calendar ${year}/${year + 1}` : `${finalLabel} ${year} Calendar`}
              countryCode={countryCode}
            />
          </div>
          </div>
        </div>

        {contentAfterTitle}

        <LandingShowcase
          entityIcon={finalIcon}
          entityLabel={finalLabel}
          year={year}
          events={filteredEvents}
          lens={lens}
          lang={lang}
          highlightDates={longWeekendHighlights}
          longWeekends={longWeekends}
          eventSlugMap={eventSlugMap}
          hideHeader
          countryCode={countryCode}
          lensLabelOverride={lensLabelOverride}
        />

        {contentAfterShowcase}

        <LanguageLinks alternates={langAlternates} currentLang={lang} />

        <LandingFooter topPages={topPages} lang={lang} />
      </main>
    </div>
  );
}
